/**
 * AIMS Algorithm Processing Service
 * Handles standardized report generation and care plan creation
 */

import DatabaseService from './databaseService';

class AIMSService {
  constructor() {
    this.cloudURL = import.meta.env.VITE_AIMS_CLOUD_API || 'https://api.aims-system.com/v2';
    this.apiKey = import.meta.env.VITE_AIMS_API_KEY || 'demo-key';
  }

  /**
   * Process P&ID Pro report through AIMS algorithms
   * @param {Object} pidReport - Report from P&ID Pro
   * @param {Object} patientInfo - Supervisor information
   * @returns {Promise<Object>} Standardized AIMS report
   */
  async processPIDReport(pidReport, patientInfo) {
    try {
      console.log(' Processing P&ID report through AIMS algorithms...');

      const processingResult = await this.runAIMSAnalysis(pidReport, patientInfo);

      return {
        success: true,
        reportId: processingResult.reportId,
        standardizedReport: processingResult.standardizedReport,
        carePlan: processingResult.carePlan,
        riskAssessment: processingResult.riskAssessment,
        recommendations: processingResult.recommendations
      };
    } catch (error) {
      console.error('ERROR: AIMS processing failed:', error);
      throw new Error(`AIMS processing failed: ${error.message}`);
    }
  }

  /**
   * Run AIMS analysis algorithms
   * @param {Object} pidData - Raw P&ID data
   * @param {Object} patientInfo - Supervisor demographics and history
   * @returns {Promise<Object>} Analysis results
   */
  async runAIMSAnalysis(pidData, patientInfo) {
    // Simulate advanced AI processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    const reportId = `aims_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Analyze brain wave patterns
    const brainWaveAnalysis = this.analyzeBrainWaves(pidData.findings);

    // Generate risk assessment
    const riskAssessment = this.assessNeurologicalRisk(brainWaveAnalysis, patientInfo);

    // Create standardized report
    const standardizedReport = this.generateStandardizedReport(brainWaveAnalysis, riskAssessment, patientInfo);

    // Generate personalized care plan
    const carePlan = this.generateCarePlan(riskAssessment, patientInfo);

    return {
      reportId,
      standardizedReport,
      carePlan,
      riskAssessment,
      recommendations: this.generateRecommendations(riskAssessment, patientInfo),
      processingMetadata: {
        algorithmVersion: 'AIMS v3.2.1',
        processedAt: new Date().toISOString(),
        processingTime: '2.1 seconds',
        confidence: 0.94
      }
    };
  }

  /**
   * Analyze brain wave patterns using AIMS algorithms
   */
  analyzeBrainWaves(pidFindings) {
    const dominantFreq = parseFloat(pidFindings.dominantFrequency) || 10.0;
    const asymmetry = parseFloat(pidFindings.asymmetryIndex) || 0.15;

    return {
      alpha: {
        frequency: dominantFreq,
        power: this.calculateAlphaPower(dominantFreq),
        symmetry: asymmetry < 0.2 ? 'Normal' : 'Asymmetric',
        reactivity: pidFindings.alphaBlockingResponse || 'Normal'
      },
      beta: {
        ratio: this.calculateBetaRatio(dominantFreq),
        distribution: 'Frontal-Central',
        coherence: 0.78
      },
      theta: {
        power: this.calculateThetaPower(dominantFreq),
        location: 'Temporal',
        significance: 'Within normal limits'
      },
      delta: {
        power: this.calculateDeltaPower(),
        sleepStages: 'N/A - Awake recording',
        pathology: 'None detected'
      },
      connectivity: {
        frontoParietal: 0.82,
        leftRight: 1 - asymmetry,
        anteriorPosterior: 0.75
      },
      abnormalities: this.detectAbnormalities(dominantFreq, asymmetry)
    };
  }

  /**
   * Assess neurological risk factors
   */
  assessNeurologicalRisk(brainWaveAnalysis, patientInfo) {
    const age = patientInfo.age || 30;
    const gender = patientInfo.gender || 'unknown';

    let riskScore = 0;
    const riskFactors = [];

    // Age-related risk
    if (age > 65) {
      riskScore += 2;
      riskFactors.push('Advanced age');
    }

    // Alpha frequency assessment
    if (brainWaveAnalysis.alpha.frequency < 8.5) {
      riskScore += 3;
      riskFactors.push('Slow alpha frequency');
    }

    // Asymmetry assessment
    if (brainWaveAnalysis.alpha.symmetry === 'Asymmetric') {
      riskScore += 2;
      riskFactors.push('Hemispheric asymmetry');
    }

    // Abnormality detection
    if (brainWaveAnalysis.abnormalities.length > 0) {
      riskScore += brainWaveAnalysis.abnormalities.length;
      riskFactors.push(...brainWaveAnalysis.abnormalities);
    }

    return {
      totalScore: riskScore,
      riskLevel: this.categorizeRisk(riskScore),
      riskFactors,
      recommendations: this.getRiskRecommendations(riskScore),
      followUpRequired: riskScore > 3,
      urgency: riskScore > 6 ? 'High' : riskScore > 3 ? 'Medium' : 'Low'
    };
  }

  /**
   * Generate standardized clinical report
   */
  generateStandardizedReport(brainWaveAnalysis, riskAssessment, patientInfo) {
    return {
      header: {
        patientName: patientInfo.name,
        patientId: patientInfo.id,
        age: patientInfo.age,
        gender: patientInfo.gender,
        reportDate: new Date().toISOString(),
        clinician: patientInfo.clinician || 'Dr. AIMS AI'
      },
      clinicalFindings: {
        dominantRhythm: `${brainWaveAnalysis.alpha.frequency} Hz alpha rhythm`,
        amplitude: 'Normal (50-100 μV)',
        distribution: 'Posterior dominant',
        reactivity: brainWaveAnalysis.alpha.reactivity,
        asymmetry: brainWaveAnalysis.alpha.symmetry,
        artifacts: 'Minimal muscle and eye movement artifacts'
      },
      quantitativeAnalysis: {
        alphaPower: brainWaveAnalysis.alpha.power,
        betaRatio: brainWaveAnalysis.beta.ratio,
        thetaPower: brainWaveAnalysis.theta.power,
        deltaActivity: brainWaveAnalysis.delta.power,
        connectivity: brainWaveAnalysis.connectivity
      },
      interpretation: {
        summary: this.generateInterpretationSummary(brainWaveAnalysis, riskAssessment),
        significance: this.getClinicialSignificance(riskAssessment),
        limitations: 'Results should be interpreted in clinical context'
      },
      conclusion: this.generateConclusion(riskAssessment)
    };
  }

  /**
   * Generate personalized care plan
   */
  generateCarePlan(riskAssessment, patientInfo) {
    const basePlan = {
      goals: [
        'Optimize brain function and cognitive performance',
        'Monitor neurological health indicators',
        'Prevent cognitive decline where applicable'
      ],
      interventions: [],
      monitoring: {
        frequency: 'Every 6 months',
        parameters: ['P&ID follow-up', 'Cognitive assessment', 'Symptom monitoring'],
        alerts: []
      },
      lifestyle: {
        exercise: 'Regular aerobic exercise 30min, 5x/week',
        sleep: '7-9 hours quality sleep nightly',
        nutrition: 'Mediterranean diet rich in omega-3 fatty acids',
        stress: 'Stress management techniques and mindfulness'
      }
    };

    // Customize based on risk level
    if (riskAssessment.riskLevel === 'High') {
      basePlan.interventions.push(
        'Immediate neurological consultation',
        'Comprehensive neuropsychological testing',
        'Consider pharmacological intervention'
      );
      basePlan.monitoring.frequency = 'Every 3 months';
      basePlan.monitoring.alerts.push('Urgent follow-up required');
    } else if (riskAssessment.riskLevel === 'Medium') {
      basePlan.interventions.push(
        'Cognitive training exercises',
        'Neurofeedback therapy consideration',
        'Regular medical check-ups'
      );
      basePlan.monitoring.frequency = 'Every 4 months';
    } else {
      basePlan.interventions.push(
        'Preventive cognitive exercises',
        'Lifestyle optimization',
        'Annual health screenings'
      );
    }

    return basePlan;
  }

  /**
   * Generate clinical recommendations
   */
  generateRecommendations(riskAssessment, patientInfo) {
    const recommendations = [];

    // Primary recommendations based on risk
    if (riskAssessment.riskLevel === 'High') {
      recommendations.push({
        type: 'urgent',
        title: 'Immediate Medical Attention',
        description: 'Schedule appointment with neurologist within 2 weeks',
        priority: 1
      });
    }

    if (riskAssessment.riskLevel === 'Medium' || riskAssessment.riskLevel === 'High') {
      recommendations.push({
        type: 'clinical',
        title: 'Follow-up P&ID',
        description: 'Repeat P&ID assessment in 3-6 months to monitor changes',
        priority: 2
      });
    }

    // Lifestyle recommendations
    recommendations.push({
      type: 'lifestyle',
      title: 'Cognitive Enhancement',
      description: 'Engage in regular mental exercises and learning activities',
      priority: 3
    });

    recommendations.push({
      type: 'monitoring',
      title: 'Sleep Quality',
      description: 'Maintain consistent sleep schedule and address any sleep disorders',
      priority: 4
    });

    return recommendations.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Helper methods for calculations
   */
  calculateAlphaPower(frequency) {
    return Math.max(0.3, Math.min(0.9, (frequency - 7) / 5));
  }

  calculateBetaRatio(alphaFreq) {
    return Math.round((15 / alphaFreq) * 100) / 100;
  }

  calculateThetaPower(alphaFreq) {
    return Math.max(0.1, Math.min(0.4, (10 - alphaFreq) / 8));
  }

  calculateDeltaPower() {
    return Math.random() * 0.2 + 0.05; // Normal awake delta is low
  }

  detectAbnormalities(frequency, asymmetry) {
    const abnormalities = [];

    if (frequency < 8) {
      abnormalities.push('Slow alpha variant');
    }
    if (asymmetry > 0.3) {
      abnormalities.push('Significant hemispheric asymmetry');
    }

    return abnormalities;
  }

  categorizeRisk(score) {
    if (score >= 7) return 'High';
    if (score >= 4) return 'Medium';
    return 'Low';
  }

  getRiskRecommendations(score) {
    if (score >= 7) return ['Immediate medical consultation', 'Comprehensive neurological workup'];
    if (score >= 4) return ['Regular monitoring', 'Lifestyle modifications'];
    return ['Preventive care', 'Annual follow-up'];
  }

  generateInterpretationSummary(brainWaveAnalysis, riskAssessment) {
    const level = riskAssessment.riskLevel.toLowerCase();
    const alpha = brainWaveAnalysis.alpha.frequency;

    return `This P&ID recording shows ${level} risk neurological patterns. ` +
           `Alpha frequency of ${alpha} Hz is ${alpha >= 9 ? 'within normal' : 'below normal'} limits. ` +
           `Overall brain wave patterns suggest ${this.getOverallAssessment(riskAssessment.riskLevel)}.`;
  }

  getClinicialSignificance(riskAssessment) {
    switch (riskAssessment.riskLevel) {
      case 'High':
        return 'Findings warrant immediate clinical attention and further investigation';
      case 'Medium':
        return 'Findings suggest need for monitoring and possible intervention';
      default:
        return 'Findings within expected range for age and demographics';
    }
  }

  generateConclusion(riskAssessment) {
    return `Based on AIMS algorithmic analysis, this supervisor presents with ${riskAssessment.riskLevel.toLowerCase()} risk patterns. ` +
           `${riskAssessment.followUpRequired ? 'Follow-up assessment is recommended.' : 'Routine monitoring is sufficient.'} ` +
           `Clinical correlation is advised for optimal supervisor care.`;
  }

  getOverallAssessment(riskLevel) {
    switch (riskLevel) {
      case 'High': return 'potential neurological concerns requiring attention';
      case 'Medium': return 'borderline patterns requiring monitoring';
      default: return 'normal neurological activity for demographic';
    }
  }

  /**
   * Save processed report to database
   * @param {Object} processedReport - Complete AIMS report
   * @param {string} clinicId - Clinic identifier
   * @param {string} patientId - Supervisor identifier
   */
  async saveProcessedReport(processedReport, clinicId, patientId) {
    try {
      const reportData = {
        id: processedReport.reportId,
        type: 'aims_analysis',
        clinicId,
        patientId,
        pidReportId: processedReport.originalQeegReportId,
        standardizedReport: processedReport.standardizedReport,
        carePlan: processedReport.carePlan,
        riskAssessment: processedReport.riskAssessment,
        recommendations: processedReport.recommendations,
        metadata: processedReport.processingMetadata,
        createdAt: new Date().toISOString(),
        status: 'completed'
      };

      await DatabaseService.add('aims_reports', reportData);
      console.log('SUCCESS: AIMS report saved to database:', reportData.id);

      return reportData;
    } catch (error) {
      console.error('ERROR: Failed to save AIMS report:', error);
      throw error;
    }
  }
}

export default new AIMSService();
